import React, { useContext, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from '../../hooks/useTranslation';
import Backdrop from '../backdrop';
import {
  LinkedPerson,
  ManagePerRelationshipResponse,
} from '../../__generated__/pge-types';
import ROUTES from '../../routes';
import colors from '../../themes/main-colors';
import { Close } from '@material-ui/icons';
import { useIsMobile } from '../../util/style-utils';
import AddUsersModal from '../ssm/post-enrollment/CoCustomer/AddUsersModal';
import AddPerson from '../account-summary/manage-alerts/AddPerson';
import PGEButton from '../buttons';
import ExternalLinkIcon from '../../static/icon_external_link.svg';
import AccountDropdown from '../account-dropdown';
import useChargePoint from './useChargePoint';
import { navigate } from '@reach/router';
import _ from 'lodash';
import { ChargePointDriver } from './ChargePointDriver';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChargePointIcon from '../../static/Chargepoint.svg';
import OpConnectIcon from '../../images/opconnect_icon.png';
import { EMAIL_REGEX } from '../../util/form-validation';
import {
  LanguageContext,
  SupportedLanguages,
} from '../../providers/LanguageProvider';
import ReactMarkdown from 'react-markdown/with-html';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      fontSize: theme.spacing(4.5),
      lineHeight: theme.typography.pxToRem(28),
      fontWeight: 'bold',
      letterSpacing: '0.720px',
      marginBottom: '30px',
      fontFamily: 'Forma-DJR-Display',
      color: colors.lightCyan,
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.spacing(3),
        marginBottom: '16px',
      },
    },
    accountBox: {
      margin: '16px 0px 32px',
      [theme.breakpoints.down('sm')]: {
        margin: '16px 0px 20px',
      },
    },
    closeBtn: {
      position: 'absolute',
      top: 10,
      right: 10,
      padding: 0,
    },
    modalBody: {
      marginTop: theme.spacing(1),
      '& > *': {
        marginTop: theme.spacing(1),
      },
    },
    removeButtonContainer: {
      marginTop: 20,
      justifyContent: 'center',
      gap: '20px',
    },
    removePersonDataModalWrapper: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '700px',
        width: '80%',
      },
    },
    removePersonDataModal: {
      padding: theme.spacing(3),
      borderRadius: '5px',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(4, 3, 3, 3),
      },
    },
    termsAndConditions: {
      fontSize: 'medium',
      '& p': {
        margin: '0',
      },
    },
    button: {
      borderRadius: '5px',
      padding: '10px 57px',
      fontWeight: 'bold',
      fontSize: '18px',
      fontFamily: 'Untitled-Sans',
      display: 'block',
      '&:hover': {
        boxShadow: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        borderRadius: '30px',
        padding: '7px 0px',
        width: '90%',
      },
    },
    buttonStyles: {
      [theme.breakpoints.down('sm')]: {
        width: '85%',
        borderRadius: '30px',
      },
    },
  }),
);

type Props = {
  path?: string;
};

export default function Chargepoint(props: Props) {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { language } = useContext(LanguageContext);

  const [showRemovePersonDialog, setShowRemovePersonDialog] = useState(false);
  const [selctedPersonForDelete, setSelctedPersonForDelete] = useState<{
    personId: string;
    personName: string;
    email: string;
    accessCode: string;
  }>();
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [removedDriverDetails, setRemovedDriverDetails] = useState({
    primeDriver: false,
    secondaryDriver: false,
    thirdDriver: false,
  });
  const [recentlyAddedPerson, setRecentlyAddedPerson] = useState();

  const {
    personList,
    refetchPerson,
    customer,
    mainPersonEmail,
    form,
    handleSubmit,
    dataLoading,
    defaultFormValues,
    handleDeletePerson,
    isIqbdAccount,
    loading,
    getIQBDStatusAuthDriver,
    chargePoint,
  } = useChargePoint();
  const [primeDriver, setPrimeDriver] = useState<LinkedPerson | undefined>();
  const [secDriver, setSecDriver] = useState<LinkedPerson | undefined>();
  const [thirdDriver, setThirdDriver] = useState<LinkedPerson | undefined>();
  const [isDataModified, setDataModified] = useState<boolean>(false);
  const [isUserEligibleToAdd, setIsUserEligibleToAdd] = useState<string>(
    'valid',
  );
  useEffect(() => {
    setPrimeDriver(
      !dataLoading &&
        personList &&
        defaultFormValues &&
        defaultFormValues.primeDriver
        ? personList.filter(
            per => per.personId === defaultFormValues.primeDriver,
          )[0]
        : undefined,
    );
    setSecDriver(
      !dataLoading &&
        personList &&
        defaultFormValues &&
        defaultFormValues.secondaryDriver
        ? personList.filter(
            per => per.personId === defaultFormValues.secondaryDriver,
          )[0]
        : undefined,
    );
    setThirdDriver(
      !dataLoading &&
        personList &&
        defaultFormValues &&
        defaultFormValues.thirdDriver
        ? personList.filter(
            per => per.personId === defaultFormValues.thirdDriver,
          )[0]
        : undefined,
    );
  }, [personList, defaultFormValues]);

  const loggedInPersonType = personList?.find(
    per => per.personId === customer?.personId,
  )?.accountRelTypeCd;

  if (loading) {
    return <Backdrop forceOpen message={t('STILL_LOADING')} />;
  }

  const getExcludePersonList = (driver: string) => {
    if (driver === 'primeDriver') {
      return personList?.filter(
        person =>
          person.name === secDriver?.name || person.name === thirdDriver?.name,
      );
    } else if (driver === 'secondaryDriver') {
      return personList?.filter(
        person =>
          person.name === primeDriver?.name ||
          person.name === thirdDriver?.name,
      );
    } else {
      return personList?.filter(
        person =>
          person.name === secDriver?.name || person.name === primeDriver?.name,
      );
    }
  };

  const isIQPCDUserExists = () => {
    return isUserEligibleToAdd === 'invalid';
  };

  const isDriverFormInvalid = () => {
    const formValues = form.values;
    let isEmailChanged = false;
    if (
      (formValues.primeDriverOpConnectEmail !== null ||
        formValues.primeDriverOpConnectEmail !== undefined) &&
      defaultFormValues.primeDriverOpConnectEmail !==
        formValues.primeDriverOpConnectEmail
    ) {
      isEmailChanged = true;
    }
    if (
      (formValues.secondaryDriverOpConnectEmail !== null ||
        formValues.secondaryDriverOpConnectEmail !== undefined) &&
      defaultFormValues.secondaryDriverOpConnectEmail !==
        formValues.secondaryDriverOpConnectEmail
    ) {
      isEmailChanged = true;
    }
    if (
      (formValues.thirdDriverOpConnectEmail !== null ||
        formValues.thirdDriverOpConnectEmail !== undefined) &&
      defaultFormValues.thirdDriverOpConnectEmail !==
        formValues.thirdDriverOpConnectEmail
    ) {
      isEmailChanged = true;
    }
    const primeDriverOpConnectEmail = form.props('primeDriverOpConnectEmail')
      .value;
    const secondaryDriverOpConnectEmail = form.props(
      'secondaryDriverOpConnectEmail',
    ).value;
    const thirdDriverOpConnectEmail = form.props('thirdDriverOpConnectEmail')
      .value;
    const isPrimaryEmail =
      !primeDriverOpConnectEmail ||
      primeDriverOpConnectEmail === '' ||
      EMAIL_REGEX.test(primeDriverOpConnectEmail as string);
    const isSecondaryEmail =
      !secondaryDriverOpConnectEmail ||
      secondaryDriverOpConnectEmail === '' ||
      EMAIL_REGEX.test(secondaryDriverOpConnectEmail as string);
    const isThirdEmail =
      !thirdDriverOpConnectEmail ||
      thirdDriverOpConnectEmail === '' ||
      EMAIL_REGEX.test(thirdDriverOpConnectEmail as string);

    const isValidEmail = isPrimaryEmail && isSecondaryEmail && isThirdEmail;
    const result = !(
      isIqbdAccount &&
      (isDataModified || isEmailChanged) &&
      isValidEmail
    );
    return result;
  };

  const isAuthorizedDriverAdded = () => {
    const formValues = form.values;
    let isAuthorizedDriver = false;
    if (formValues.primeDriverAccessCode !== undefined || null) {
      return (isAuthorizedDriver = true);
    }
  };

  return (
    <Grid container style={{ justifyContent: 'center' }}>
      <div style={{ width: '100%' }}>
        <Typography className={classes.heading}>{t('IQPCD_HEADER')}</Typography>
        <Box className={classes.accountBox}>
          <AccountDropdown />
        </Box>
      </div>

      <Paper
        style={{
          width: 'inherit',
          padding: isMobile ? '20px' : '12px 30px 30px 30px',
          backgroundColor: colors.riverBlue,
          boxShadow: '0px 3px 6px #0000004D',
        }}
      >
        <Accordion
          style={{
            boxShadow: 'none',
            background: colors.riverBlue,
          }}
          defaultExpanded={!isAuthorizedDriverAdded()}
        >
          {isIqbdAccount && (
            <AccordionSummary
              style={{ padding: '0' }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Typography variant="h2">
                {t('CHARGEPOINT_ACCORDION_HEADING')}
              </Typography>
            </AccordionSummary>
          )}
          <AccordionDetails style={{ padding: '0', display: 'block' }}>
            <Grid style={{ display: 'block' }}>
              <Grid style={{ marginBottom: '20px' }}>
                {isIqbdAccount ? (
                  <Typography style={{ margin: '10px 0px' }}>
                    <ReactMarkdown
                      source={t('CHARGEPOINT_HEADING_CONTENT', {
                        PUBLIC_PAGE_LINK: ROUTES.EV_PUBLIC_DISCOUNT_PAGE,
                        CHARGING_PAGE_LINK: ROUTES.EV_PUBLIC_CHARGING_PAGE,
                      })}
                      escapeHtml={false}
                    />
                  </Typography>
                ) : (
                  <Typography style={{ margin: '10px 0px' }}>
                    {t('CHARGEPOINT_HEADING_INACTIVE_CONTENT')}
                  </Typography>
                )}
              </Grid>

              <Grid
                style={{
                  background: colors.white,
                  margin: isMobile
                    ? '20px 0px 10px 0px'
                    : '20px 40px 20px 40px',
                  padding: '10px',
                  maxHeight: '120px',
                  width: 'auto',
                }}
              >
                {isIqbdAccount ? (
                  <Typography
                    variant="h2"
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                    }}
                  >
                    {t('CHARGEPOINT_IQBD_ENROLLED')}
                  </Typography>
                ) : (
                  <Typography
                    variant="h2"
                    style={{
                      textAlign: 'center',
                      margin: '5px',
                    }}
                  >
                    {t('CHARGEPOINT_IQBD_NOT_ENROLLED')}
                  </Typography>
                )}
              </Grid>
            </Grid>
            {isIqbdAccount ? (
              <>
                <Grid style={{ display: 'block' }}>
                  <Typography variant="h2">
                    {t('CHARGEPOINT_LETS_CONNECTED_HEADING')}
                  </Typography>
                  <Typography>
                    {richT('CHARGEPOINT_LETS_CONNECTED_SUBHEADING')}
                  </Typography>
                  <Grid
                    style={{ display: 'flex', margin: '10px', gap: '15px' }}
                  >
                    {language === SupportedLanguages.English ? (
                      <ChargePointIcon
                        style={{
                          width: isMobile ? '60px' : '35px',
                          height: '35px',
                        }}
                      />
                    ) : (
                      <ChargePointIcon
                        style={{
                          width: isMobile ? '85px' : '43px',
                          height: '40px',
                        }}
                      />
                    )}

                    <Typography style={{ alignContent: 'center' }}>
                      {t('CHARGEPOINT_ACCOUNT_REGISTER_TEXT')}
                      <a
                        style={{ marginLeft: '3px' }}
                        href="https://na.chargepoint.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('HERE_ONLY')}
                      </a>
                      <ExternalLinkIcon
                        style={{
                          margin: '0px 0px -5px 6px',
                          width: '13px',
                        }}
                      />
                    </Typography>
                  </Grid>
                  <Grid
                    style={{ display: 'flex', margin: '10px ', gap: '15px' }}
                  >
                    <img
                      src={OpConnectIcon}
                      style={{
                        width: '35px',
                        height: '35px',
                        marginTop: '10px',
                      }}
                    />

                    <Typography style={{ alignContent: 'center' }}>
                      {t('CHARGEPOINT_OPCONNECT_REGISTER_TEXT')}
                      <a
                        style={{ marginLeft: '3px' }}
                        href={ROUTES.APPLE_APP_LINK}
                        target="_blank"
                      >
                        {t('APPLESTORE')}
                      </a>
                      <ExternalLinkIcon
                        style={{
                          margin: '0px 0px -5px 6px',
                          width: '13px',
                        }}
                      />
                      {t('OR')}
                      <a
                        style={{ marginLeft: '3px' }}
                        href={ROUTES.GOOGLE_APP_LINK}
                        target="_blank"
                      >
                        {t('GOOGLEPLAY')}
                      </a>
                      <ExternalLinkIcon
                        style={{
                          margin: '0px 0px -5px 6px',
                          width: '13px',
                        }}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </>
            ) : (
              <></>
            )}
          </AccordionDetails>
        </Accordion>
        {isIqbdAccount ? (
          <>
            <Grid>
              <Typography variant="h3" style={{ marginTop: '20px' }}>
                {t('CHARGEPOINT_ASSIGN_DRIVER_HEADING')}
              </Typography>

              <Box style={{ margin: '10px 0px' }}>
                <Typography color={'textPrimary'}>
                  {richT('IQBD_ASSIGN_DRIVERS_LEVERAGE', {
                    ADD_DRIVER_LINK: ROUTES.MANAGE_ALERTS,
                  })}
                </Typography>
                <Box
                  style={{
                    justifyContent: 'space-between',
                    marginTop: '20px',
                  }}
                >
                  {['primeDriver', 'secondaryDriver', 'thirdDriver'].map(
                    driver => {
                      return (
                        <ChargePointDriver
                          headerInfo={
                            driver === 'primeDriver'
                              ? 'CHARGEPOINT_ASSIGN_DRIVER_ONE_HEADING'
                              : driver === 'secondaryDriver'
                              ? 'CHARGEPOINT_ASSIGN_DRIVER_TWO_HEADING'
                              : 'CHARGEPOINT_ASSIGN_DRIVER_THREE_HEADING'
                          }
                          driverType={driver}
                          driverEmail={`${driver}OpConnectEmail`}
                          defaultFormValues={defaultFormValues}
                          setDriverInfo={driverInfo => {
                            if (driver === 'primeDriver') {
                              setPrimeDriver(driverInfo);
                              if (
                                defaultFormValues.primeDriver !==
                                  driverInfo?.personId ||
                                defaultFormValues.primeDriverOpConnectEmail !==
                                  driverInfo?.email
                              ) {
                                setDataModified(true);
                              }
                            } else if (driver === 'secondaryDriver') {
                              setSecDriver(driverInfo);
                              if (
                                defaultFormValues.secondaryDriver !==
                                  driverInfo?.personId ||
                                defaultFormValues.secondaryDriverOpConnectEmail !==
                                  driverInfo?.email
                              ) {
                                setDataModified(true);
                              }
                            } else {
                              setThirdDriver(driverInfo);
                              if (
                                defaultFormValues.thirdDriver !==
                                  driverInfo?.personId ||
                                defaultFormValues.thirdDriverOpConnectEmail !==
                                  driverInfo?.email
                              ) {
                                setDataModified(true);
                              }
                            }
                          }}
                          currentPerson={
                            driver === 'primeDriver'
                              ? primeDriver
                              : driver === 'secondaryDriver'
                              ? secDriver
                              : thirdDriver
                          }
                          excludePersonList={getExcludePersonList(driver)}
                          loggedInPersonType={loggedInPersonType as string}
                          setShowRemovePersonDialog={isOpen => {
                            setShowRemovePersonDialog(isOpen);
                          }}
                          setSelctedPersonForDelete={data => {
                            setSelctedPersonForDelete(data);
                          }}
                          form={form}
                          personList={personList}
                          getIQBDStatusAuthDriver={getIQBDStatusAuthDriver}
                          setIsUserEligibleToAdd={data => {
                            (driver === 'primeDriver' ||
                              (driver === 'secondaryDriver' &&
                                form.values.secondaryDriver !== undefined)) &&
                              setDataModified(false);
                            setIsUserEligibleToAdd(data);
                          }}
                          chargePoint={
                            chargePoint?.getChargePointPersons as ManagePerRelationshipResponse
                          }
                          isUserEligibleToAdd={isUserEligibleToAdd}
                          removedDriverDetails={removedDriverDetails}
                          setRemovedDriverDetails={setRemovedDriverDetails}
                        />
                      );
                    },
                  )}
                </Box>
              </Box>
              <Typography className={classes.termsAndConditions}>
                <ReactMarkdown
                  source={t('CHARGEPOINT_TERMS_CONDITIONS', {
                    CHARGEPOINT_TERMS_AND_CONDITIONS:
                      ROUTES.CHARGEPOINT_TERMS_CONDITIONS_LINK,
                  })}
                  escapeHtml={false}
                />
              </Typography>
            </Grid>
            <Grid>
              {showAddPersonModal && (
                <Grid>
                  <AddUsersModal
                    onClose={() => setShowAddPersonModal(false)}
                    open={showAddPersonModal}
                    title={t('ADD_PERSON_MODEL_TITLE')}
                    isDynamicClass={true}
                    mainTitle={t('ADD_AN_ACCOUNT_USER')}
                  >
                    <AddPerson
                      setShowAddPersonModal={setShowAddPersonModal}
                      refetchPerson={refetchPerson}
                      setRecentlyAddedPerson={setRecentlyAddedPerson}
                      mainPersonEmail={mainPersonEmail}
                      loggedInPerson={customer?.personId}
                      loggedInPersonName={customer?.personName}
                    />
                  </AddUsersModal>
                </Grid>
              )}
              {showRemovePersonDialog && (
                <Dialog
                  open={showRemovePersonDialog}
                  onClose={() => setShowRemovePersonDialog(false)}
                  scroll={'body'}
                  aria-labelledby="delete-person-modal"
                  aria-describedby="delete-person-modal"
                  className={classes.removePersonDataModalWrapper}
                  disableScrollLock
                >
                  <Paper className={classes.removePersonDataModal}>
                    <Box>
                      <Grid container justify="space-between">
                        <Typography
                          variant={'body1'}
                          gutterBottom
                          style={{ paddingRight: '24px' }}
                        >
                          {''}
                        </Typography>

                        <IconButton
                          aria-label="close"
                          color="inherit"
                          onClick={() => setShowRemovePersonDialog(false)}
                          className={classes.closeBtn}
                        >
                          <Close />
                        </IconButton>
                      </Grid>

                      <Box className={classes.modalBody}>
                        <Typography variant={'h3'}>
                          {richT('REMOVE_PERSON_CHARGEPOINT', {
                            PERSON_NAME: selctedPersonForDelete?.personName,
                          })}
                        </Typography>

                        <Grid
                          className={classes.removeButtonContainer}
                          container
                          spacing={2}
                        >
                          <PGEButton
                            size="large"
                            color="secondary"
                            variant="outlined"
                            className={classes.buttonStyles}
                            onClick={() => setShowRemovePersonDialog(false)}
                          >
                            {t('CANCEL')}
                          </PGEButton>

                          <PGEButton
                            size="large"
                            className={classes.buttonStyles}
                            onClick={() => {
                              void handleDeletePerson(
                                selctedPersonForDelete?.personId!,
                                selctedPersonForDelete?.personName!,
                                selctedPersonForDelete?.email!,
                                selctedPersonForDelete?.accessCode!,
                              );
                              setShowRemovePersonDialog(false);
                            }}
                          >
                            {t('CONFIRM')}
                          </PGEButton>
                        </Grid>
                      </Box>
                    </Box>
                  </Paper>
                </Dialog>
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid
              style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
              }}
            >
              <Grid style={{ alignContent: 'center' }}>
                <Typography
                  style={{ fontWeight: 'bold', alignContent: 'center' }}
                >
                  {t('CHARGEPOINT_IQBD_NOT_ENROLLED_QUESTION')}
                </Typography>
              </Grid>
              <Grid>
                <Button
                  variant={'contained'}
                  color={'primary'}
                  style={{ width: '140px' }}
                  onClick={() => navigate(ROUTES.IQBD_FORM)}
                >
                  {t('CHARGEPOINT_IQBD_NOT_ENROLLED_BUTTON')}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      <Grid
        container
        css={{
          display: 'flex',
          gap: isMobile ? '17px ' : '32px',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-end',
          marginTop: '24px',
        }}
      >
        <Button
          variant={'outlined'}
          color={'primary'}
          className={classes.button}
          onClick={() => navigate(ROUTES.ACCOUNT)}
        >
          {t('BACK')}
        </Button>

        <Button
          type={'submit'}
          variant={'contained'}
          color={'primary'}
          className={classes.button}
          disabled={isDriverFormInvalid() || isIQPCDUserExists()}
          css={{
            backgroundColor: colors.orange,
            color: colors.white,
            '&:hover': {
              backgroundColor: colors.orange,
              '&:hover': {
                backgroundColor: colors.orange,
              },
              color: colors.white,
            },
          }}
          onClick={() => handleSubmit(primeDriver, secDriver, thirdDriver)}
        >
          {t('SUBMIT')}
        </Button>
      </Grid>
    </Grid>
  );
}
